import React, { createContext, useContext, useState, useCallback } from 'react';

const ConfirmContext = createContext();

export const ConfirmProvider = ({ children }) => {
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    message: '',
    type: 'warning', // 'warning', 'danger', 'info'
    resolve: null,
  });

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        message: options.message,
        type: options.type || 'warning',
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => {
    if (confirmState.resolve) confirmState.resolve(true);
    setConfirmState({ isOpen: false, message: '', type: 'warning', resolve: null });
  };

  const handleCancel = () => {
    if (confirmState.resolve) confirmState.resolve(false);
    setConfirmState({ isOpen: false, message: '', type: 'warning', resolve: null });
  };

  const getIconAndStyle = (type) => {
    switch (type) {
      case 'info':
        return { icon: 'ℹ️', bg: '#eff6ff', color: '#3b82f6', btn: '#3b82f6' };
      case 'danger':
        return { icon: '🗑️', bg: '#fef2f2', color: '#ef4444', btn: '#ef4444' };
      case 'warning':
      default:
        return { icon: '⚠️', bg: '#fffbeb', color: '#f59e0b', btn: '#f59e0b' };
    }
  };

  const theme = getIconAndStyle(confirmState.type);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      
      {confirmState.isOpen && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={{ ...styles.iconContainer, backgroundColor: theme.bg }}>
              <span style={styles.icon}>{theme.icon}</span>
            </div>
            <p style={styles.message}>{confirmState.message}</p>
            <div style={styles.buttons}>
              <button style={styles.cancelBtn} onClick={handleCancel}>キャンセル</button>
              <button style={{ ...styles.confirmBtn, backgroundColor: theme.btn }} onClick={handleConfirm}>OK</button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)', // Darker, softer slate overlay
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '380px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    animation: 'modalSlideIn 0.2s ease-out forwards',
  },
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  icon: {
    fontSize: '24px',
  },
  message: {
    margin: '0 0 24px 0',
    fontSize: '16px',
    color: '#334155',
    fontWeight: '500',
    lineHeight: '1.5',
    whiteSpace: 'pre-line',
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    width: '100%',
  },
  cancelBtn: {
    flex: 1,
    padding: '10px 0',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    color: '#64748b',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  confirmBtn: {
    flex: 1,
    padding: '10px 0',
    borderRadius: '8px',
    border: 'none',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  }
};
